import { lazy, Suspense, useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "@tanstack/react-router";
import { Search, X, Loader2 } from "lucide-react";
import type { SearchHit, SearchSuggestion } from "@/lib/smartSearch";

const SmartSearchPanel = lazy(() => import("@/components/search/SmartSearchPanel"));
const loadSmartSearch = () => import("@/lib/smartSearch").then((module) => module.SmartSearch);

const FIELD =
  "h-10 w-full rounded-full border border-border/60 bg-background/95 pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground shadow-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/30";

function useSubmit(onDone?: () => void) {
  const navigate = useNavigate();
  return (value: string) => (e: React.FormEvent) => {
    e.preventDefault();
    const q = value.trim();
    if (q.length < 2) return;
    onDone?.();
    void navigate({ to: "/search", search: { q, facet: undefined } });
  };
}

function useDebounce<T>(value: T, delay = 180) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function useClickOutside(ref: React.RefObject<HTMLElement | null>, onClickOutside: () => void) {
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (!ref.current || ref.current.contains(e.target as Node)) return;
      onClickOutside();
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [ref, onClickOutside]);
}

interface DropdownState {
  query: string;
  hits: SearchHit[];
  suggestions: SearchSuggestion[];
  loading: boolean;
}

const EMPTY_STATE: DropdownState = {
  query: "",
  hits: [],
  suggestions: [],
  loading: false,
};

function useSmartSearch() {
  const [state, setState] = useState<DropdownState>(EMPTY_STATE);
  const [busy, setBusy] = useState(false);
  const warmed = useRef(false);
  const warm = useCallback(() => {
    // Only the search controls call this. Ordinary browsing must not download
    // or parse the entire search corpus. The engine shares its in-flight cache.
    if (warmed.current) return;
    warmed.current = true;
    void loadSmartSearch().then((search) => search.preload()).catch(() => {
      warmed.current = false;
    });
  }, []);

  const run = useCallback(async (query: string) => {
    const q = query.trim();
    if (q.length < 2) {
      setState(EMPTY_STATE);
      return;
    }
    setBusy(true);
    try {
      const SmartSearch = await loadSmartSearch();
      const [result, suggestions] = await Promise.all([
        SmartSearch.search(q, { limit: 25 }),
        SmartSearch.suggestWithCounts(q, 6),
      ]);
      setState({
        query: q,
        hits: result.hits,
        suggestions,
        loading: false,
      });
    } catch {
      setState({ query: q, hits: [], suggestions: [], loading: false });
    } finally {
      setBusy(false);
    }
  }, []);

  return { state, busy, run, warm };
}

interface SearchDropdownProps {
  open: boolean;
  state: DropdownState;
  busy: boolean;
  onSuggestionClick: (q: string) => void;
  inputRect: DOMRect | null;
}

/** Desktop dropdown rendered with fixed positioning so it always escapes the header. */
function SearchDropdown({
  open,
  state,
  busy,
  onSuggestionClick,
  inputRect,
}: SearchDropdownProps) {
  if (!open) return null;

  const style = inputRect
    ? {
        position: "fixed" as const,
        top: `${inputRect.bottom + 6}px`,
        left: `${inputRect.left}px`,
        width: `${Math.min(inputRect.width, window.innerWidth - 32)}px`,
      }
    : {};

  const panel = (
    <div
      className="z-[9999]"
      id="header-search-results"
      role="listbox"
      aria-label="Search results"
      style={style}
      onMouseDown={(e) => {
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
      }}
    >
      {busy && state.hits.length === 0 && state.suggestions.length === 0 ? (
        <div className="flex items-center justify-center rounded-xl border bg-popover py-6 shadow-xl">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <Suspense fallback={<div className="h-16 rounded-xl border bg-popover shadow-xl" aria-hidden />}>
          <SmartSearchPanel
            query={state.query}
            hits={state.hits}
            suggestions={state.suggestions}
            loading={busy}
            visibleCount={8}
            onSuggestionClick={onSuggestionClick}
            linkToResultsPage
            stacked
            hideSuggestions
            showAllAtTop
            scrollable
          />
        </Suspense>
      )}
    </div>
  );

  // Portal to <body> so no header/hero stacking context can trap it.
  return typeof document === "undefined" ? panel : createPortal(panel, document.body);
}

/** Inline, centered site search for the desktop header row. */
export function HeaderSearchInline() {
  const [value, setValue] = useState("");
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const debounced = useDebounce(value, 180);
  const { state, busy, run, warm } = useSmartSearch();
  const formRef = useRef<HTMLFormElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [inputRect, setInputRect] = useState<DOMRect | null>(null);
  const submit = useSubmit(() => setOpen(false));
  const location = useLocation();

  useEffect(() => {
    if (debounced.trim().length >= 2) {
      void run(debounced);
    } else {
      run("");
    }
  }, [debounced, run]);

  useEffect(() => {
    function updateRect() {
      if (inputRef.current) setInputRect(inputRef.current.getBoundingClientRect());
    }
    updateRect();
    window.addEventListener("resize", updateRect);
    window.addEventListener("scroll", updateRect, { passive: true });
    return () => {
      window.removeEventListener("resize", updateRect);
      window.removeEventListener("scroll", updateRect);
    };
  }, []);

  useClickOutside(formRef, () => setOpen(false));

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Close the dropdown when the user navigates to a result.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const showDropdown = open && (focused || busy || state.hits.length > 0 || state.suggestions.length > 0);

  return (
    <>
      <form
        ref={formRef}
        onSubmit={submit(value)}
        role="search"
        className="relative z-30 mx-auto hidden w-full min-w-0 max-w-xl items-center xl:flex"
      >
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          ref={inputRef}
          type="search"
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setOpen(true);
          }}
          onFocus={() => {
            warm();
            setFocused(true);
            setOpen(true);
          }}
          onBlur={() => setFocused(false)}
          placeholder="Search RVs, guides, blog…"
          aria-label="Search the site"
          role="combobox"
          aria-haspopup="listbox"
          aria-autocomplete="list"
          aria-controls="header-search-results"
          aria-expanded={showDropdown}
          className={FIELD}
        />
      </form>
      <SearchDropdown
        open={showDropdown}
        state={state}
        busy={busy}
        onSuggestionClick={(q) => {
          setValue(q);
          setOpen(true);
          void run(q);
        }}
        inputRect={inputRect}
      />
    </>
  );
}

/** Mobile/tablet search: icon toggle plus a full-width overlay row. */
export function HeaderSearchMobile({ className }: { className?: string }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const debounced = useDebounce(value, 180);
  const { state, busy, run, warm } = useSmartSearch();
  const inputRef = useRef<HTMLInputElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const submit = useSubmit(() => setOpen(false));
  const location = useLocation();

  useEffect(() => {
    if (open) {
      warm();
      inputRef.current?.focus();
    }
  }, [open, warm]);

  useEffect(() => {
    if (debounced.trim().length >= 2) {
      void run(debounced);
    } else {
      run("");
    }
  }, [debounced, run]);

  useClickOutside(overlayRef, () => setOpen(false));

  // Close the overlay when the user navigates to a result.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const showDropdown = open && (busy || state.hits.length > 0 || state.suggestions.length > 0);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? "Close search" : "Open search"}
        aria-expanded={open}
        className={`relative z-40 grid h-12 w-12 min-h-[48px] min-w-[48px] shrink-0 place-items-center rounded-full transition-colors hover:bg-background/10 xl:hidden ${className ?? ""}`}
      >
        {open ? <X className="h-6 w-6" /> : <Search className="h-6 w-6" />}
      </button>

      {open ? (
        <div
          ref={overlayRef}
          className="absolute left-0 right-0 top-full z-40 border-b border-border/40 bg-secondary px-4 py-3 shadow-lg xl:hidden"
        >
          <form onSubmit={submit(value)} role="search" className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              ref={inputRef}
              type="search"
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
              }}
              placeholder="Search RVs, guides, blog…"
              aria-label="Search the site"
              className={FIELD}
            />
          </form>

          <div
            className="mt-2"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              if ((e.target as HTMLElement).closest("a")) {
                setOpen(false);
              }
            }}
          >
            {busy && state.hits.length === 0 && state.suggestions.length === 0 ? (
              <div className="flex items-center justify-center rounded-xl border bg-popover py-6 shadow-xl">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <SmartSearchPanel
                query={state.query}
                hits={state.hits}
                suggestions={state.suggestions}
                loading={busy}
                visibleCount={8}
                onSuggestionClick={(q) => {
                  setValue(q);
                  void run(q);
                }}
                linkToResultsPage
                stacked
                hideSuggestions
                showAllAtTop
                scrollable
              />
            )}
          </div>
        </div>
      ) : null}
    </>
  );
}
